import AuthorisedContainer from "components/AuthorisedContainer";
import { PlusIcon } from "components/Icons";
import { PermissionSetMap } from "constants/RBACConstants";
import usePaginationControls from "hooks/usePaginationControls";
import useTableCustomizationHelper from "hooks/useTableCustomizationHelper";
import useTagTableDetails, { Tag } from "hooks/useTagTableData";
import { isEmpty, isNumber, noop } from "lodash";
import React, {
  Dispatch,
  ReactNode,
  RefObject,
  SetStateAction,
  useEffect,
  useMemo,
} from "react";
import {
  getFindingDetailsTagsSortInfo,
  setFindingDetailsTagsSortInfo,
} from "utils/localStorageUtils";
import Sorters, { SorterFunction } from "utils/sorters";

import ACTable from "ac-components/ACTable/ACTable";
import ACButton from "ac-components/ac-button/ACButton";
import ACResizableColumnCell from "ac-components/ac-resizable-column-cell/ACResizableColumnCell";
import { ACTabItem } from "ac-components/ac-tabs/ACTabs";

import "./ACTagTable.less";

interface TagsMetaMap {
  [key: string]: string[];
}
interface ACTagTableProps {
  source: (string | Tag)[];
  staticTags?: string[];
  editable: boolean;
  onChange: (
    source: (string | Tag)[],
    deleted: { deleted?: string | Tag },
  ) => void;
  getAllEvents: (source: (string | Tag)[], action: string) => void;
  extras: ReactNode;
  mentionOptions: string[];
  disableAccessText: string;
  tagsRef: RefObject<{ [key: string]: any }>;
  preventKeys: string[];
  accountAdminOnlyKeys?: string[];
  className: string;
  width: string;
  tagsMetaMap: TagsMetaMap;
  tabsList: ACTabItem[];
  permission: string;
  noStyle: boolean;
  loading?: boolean;
  setEditingTags?: Dispatch<SetStateAction<boolean>>;
  disableEditActions?: boolean;
  [key: string]: any;
  maxTags?: number;
  prefKey: string;
  searchTerm?: string;
}
export default function ACTagTable({
  source,
  staticTags,
  editable = false,
  onChange = noop,
  getAllEvents = noop,
  mentionOptions,
  disableAccessText,
  tagsRef,
  preventKeys = [],
  accountAdminOnlyKeys = [],
  width = "200px",
  permission,
  noStyle = false,
  loading,
  setEditingTags,
  disableEditActions,
  maxTags,
  prefKey,
  searchTerm,
}: ACTagTableProps) {
  const {
    dataSource,
    wrapperRef,
    currentRowRef,
    onAdd,
    columns,
    actionRenderer,
  } = useTagTableDetails({
    source,
    onChange,
    getAllEvents,
    staticTags,
    preventKeys,
    accountAdminOnlyKeys,
    mentionOptions,
    tagsRef,
    editable,
    permission,
    width,
    disableAccessText,
    setEditingTags,
    disableEditActions,
  });
  const savedSortInfo = getFindingDetailsTagsSortInfo();
  const { onSortChange, sortInfo } = usePaginationControls({
    defaultSortInfo: savedSortInfo,
  });

  useEffect(() => {
    setFindingDetailsTagsSortInfo(sortInfo);
  }, [sortInfo]);

  const {
    //@ts-expect-error
    requiredError,
    //@ts-expect-error
    duplicateError,
    //@ts-expect-error
    colonError,
    //@ts-expect-error
    reservedKeyError,
    //@ts-expect-error
    accountAdminOnlyError,
    //@ts-expect-error
    lengthError,
  } = currentRowRef.current;

  // TODO - move all logic in hook so that we can use if separately for finding tags
  const sortedDataSource = useMemo(() => {
    const newAddedRecord = dataSource
      ? dataSource.filter((item) => item.newRecordAdded)
      : [];
    const oldRecord = dataSource
      ? dataSource.filter((item) => !item.newRecordAdded)
      : [];
    if (!isEmpty(sortInfo)) {
      const sorters: { [key: string]: SorterFunction<Tag> } = {
        key: Sorters.STRING("key"),
        value: Sorters.STRING("value"),
      };
      const sortedStats = [...oldRecord];
      sortedStats.sort((a, b) => {
        let comparison = 0;
        for (const i in sortInfo) {
          if (sorters[i]) {
            comparison =
              comparison ||
              sorters[i](a, b) * (sortInfo[i] === "ascend" ? 1 : -1);
          }
        }
        return comparison;
      });
      return [...sortedStats, ...newAddedRecord];
    }
    return [...oldRecord, ...newAddedRecord];
  }, [sortInfo, dataSource]);

  const searchFilteredDataSource = useMemo(() => {
    if (!searchTerm) return sortedDataSource;
    const term = searchTerm.toLowerCase();
    return sortedDataSource.filter(
      (tag) =>
        tag.isEdit ||
        tag.newRecordAdded ||
        tag.key?.toLowerCase().includes(term) ||
        tag.value?.toLowerCase().includes(term),
    );
  }, [sortedDataSource, searchTerm]);

  const isLimitReached =
    isNumber(maxTags) && dataSource && dataSource.length >= maxTags;

  const { columns: customizedColumns } = useTableCustomizationHelper<Tag>({
    columns,
    prefKey,
    skipColumnVisibility: true,
    draggable: false,
  });

  return (
    <div ref={wrapperRef} className={noStyle ? "" : "ac-tag-table"}>
      <ACTable<Tag>
        fullPage
        dataSource={searchFilteredDataSource}
        pagination={false}
        columns={customizedColumns}
        onSortChange={onSortChange}
        sortInfo={sortInfo}
        rowKey="id"
        loading={loading}
        //@ts-expect-error Type '(record: Tag, rowIndex: number | undefined) => { "data-automation-row-key": number | undefined; }' is not assignable to type 'GetComponentProps<Tag>'
        onRow={(record, rowIndex) => ({
          "data-automation-row-key": rowIndex,
        })}
        footer={() =>
          editable && (
            <>
              <div
                className="danger"
                style={{
                  width: "70%",
                }}
              >
                {requiredError
                  ? "Please fill all the required field"
                  : duplicateError
                    ? "Please enter unique key and value pair"
                    : colonError
                      ? "The field should not contain colon"
                      : reservedKeyError
                        ? "This key is reserved"
                        : accountAdminOnlyError
                          ? "Only Account Admins can add/edit/remove armorcode.group tags"
                          : lengthError
                            ? "key+value length should be less than 996 characters."
                            : ""}
                {!requiredError && !duplicateError && isLimitReached
                  ? `Maximum limit of ${maxTags} tags reached.`
                  : ""}
              </div>
              <AuthorisedContainer
                permission={
                  PermissionSetMap[permission as keyof typeof PermissionSetMap]
                }
              >
                <ACButton
                  type="outline"
                  onClick={onAdd}
                  size="sm"
                  leftIcon={<PlusIcon />}
                  disabled={disableEditActions || loading || isLimitReached}
                >
                  Add Tag
                </ACButton>
              </AuthorisedContainer>
            </>
          )
        }
        components={{
          header: {
            cell: ACResizableColumnCell,
          },
        }}
        computeTableWidth
        autoEnableScroll
        actionRender={editable ? actionRenderer : undefined}
        actionColumnWidth={100}
      />
    </div>
  );
}
